import React from "react";
import {
  PHASES,
  SCALE_MAX,
  phaseAverage,
  blindSpots,
  hasTeamView,
  scoreColor,
} from "@/data/coachingData";
import { useI18n } from "@/i18n";
import { ArrowUp, ArrowDown, Users2 } from "lucide-react";

function Bar({ value, color }) {
  return (
    <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
      <span
        className="block h-full rounded-full"
        style={{ width: `${(value / SCALE_MAX) * 100}%`, background: color }}
      />
    </span>
  );
}

export function ThreeSixtyView({ tiers, scores, teamScores }) {
  const { t } = useI18n();
  const withTeam = tiers.filter((tier) => hasTeamView(teamScores, tier.key));

  if (withTeam.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">{t("t360.empty")}</p>;
  }

  return (
    <div className="space-y-6" data-testid="three-sixty-view">
      {withTeam.map((tier) => {
        const spots = blindSpots(scores, teamScores, tier.key);
        const blind = spots.filter((s) => s.delta >= 1).slice(0, 4);
        const hidden = spots.filter((s) => s.delta <= -1).slice(-4).reverse();
        return (
          <div
            key={tier.key}
            className="print-card rounded-xl border border-slate-200 bg-white p-5"
            data-testid={`t360-${tier.key}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <Users2 size={16} className="text-indigo-600" />
              <h4 className="font-heading text-base font-bold text-slate-900 print-text">
                {t(`tier.${tier.key}`)}
              </h4>
            </div>

            <div className="space-y-2.5 mb-5">
              {PHASES.map((p) => {
                const self = phaseAverage(scores, tier.key, p.key);
                const team = phaseAverage(teamScores, tier.key, p.key);
                return (
                  <div key={p.key} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-xs text-slate-600 truncate">
                      {t(`phase.${p.key}.title`).split(" · ").pop()}
                    </span>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Bar value={self} color="#6366F1" />
                        <span className="w-7 text-right text-[11px] font-mono text-indigo-700">
                          {self.toFixed(1)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bar value={team} color="#F59E0B" />
                        <span className="w-7 text-right text-[11px] font-mono text-amber-300">
                          {team.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center gap-4 pt-1 text-[11px]">
                <span className="flex items-center gap-1.5 text-indigo-700">
                  <span className="h-2 w-2 rounded-sm bg-indigo-500" /> {t("t360.selfView")}
                </span>
                <span className="flex items-center gap-1.5 text-amber-300">
                  <span className="h-2 w-2 rounded-sm bg-amber-500" /> {t("t360.teamView")}
                </span>
              </div>
            </div>

            {blind.length === 0 && hidden.length === 0 ? (
              <p className="text-xs text-slate-500">{t("t360.aligned")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { items: blind, label: t("t360.blindSpots"), color: "#F43F5E", Icon: ArrowUp },
                  { items: hidden, label: t("t360.hidden"), color: "#10B981", Icon: ArrowDown },
                ].map(({ items, label, color, Icon }) =>
                  items.length ? (
                    <div key={label}>
                      <p
                        className="text-[10px] uppercase tracking-widest font-bold mb-1.5"
                        style={{ color }}
                      >
                        {label}
                      </p>
                      <ul className="space-y-1">
                        {items.map((s) => (
                          <li key={s.key} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-slate-700 truncate print-text">{t(`comp.${s.key}`)}</span>
                            <span className="flex items-center gap-1 font-mono shrink-0" style={{ color }}>
                              <Icon size={11} />
                              {Math.abs(s.delta).toFixed(1)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
